<template>
  <div class="global-wrapper" :class="globalWrapDynamicClass">
    <top-banner />
    <a-sticky
      :id="$options.consts.TOP_STICKY_CONTAINER_ID"
      class="top-sticky-container"
    >
      <a-header
        :last-modified="headerMenuItemsLastModified"
        :is-desktop-header-mounted.sync="isDesktopHeaderMounted"
        :is-mobile-header-mounted.sync="isMobileHeaderMounted"
      />
      <a-ticker-widget :class="tickerDynamicClass" />
    </a-sticky>
    <div class="page-wrap" :class="dynamicPageWrapClass">
      <div
        class="content-wrapper"
        :data-ref="$options.consts.REFS.CONTENT_WRAPPER"
      >
        <nuxt ref="nuxt" />
      </div>
      <a-visibility
        v-if="isPageWithSidebar"
        hide
        :on="breakpointsToHideSidebarOn"
        :keep-on-client="false"
      >
        <div class="sidebar">
          <a-sidebar />
        </div>
      </a-visibility>
    </div>

    <a-news-letter-modal />

    <a-live-bytes-modal />

    <a-survey-popup />

    <!-- Video banner shouldn't be lazy loaded to allow the sticky widget to appear -->
    <a-layout-video-banner
      v-if="isVideoBannerVisible"
      :video-banner-type="videoBannerType"
    />
    <a-footer :last-modified="footerLastModified" />

    <!--lazy-->
    <a-auth-form />

    <a-cross-domain-cookies />

    <a-device-id-cookie-iframe />

    <a-google-analytics-cookies-iframe />

    <client-only>
      <a-error-handler-notifier v-if="isDevelopment" />
    </client-only>

    <a-char-width />

    <gtm-no-script />
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { COOKIE } from 'enums/cookies'
import mixins from '@/utils/mixins'
import AHeader from '@/components/AHeader'
import AAuthForm from 'shared/AAuthForm/lazy'
import ACrossDomainCookies from '@/components/_layout/ACrossDomainCookies'
import ATickerWidget from 'shared/TradingViewWidgets/ATickerWidget'
import ADeviceIdCookieIframe from '@/components/_layout/ADeviceIdCookieIframe'
import AGoogleAnalyticsCookiesIframe from '@/components/_layout/AGoogleAnalyticsCookiesIframe'
import ANewsLetterModal from 'shared/ANewsLetterModal'
import ALiveBytesModal from 'shared/ALiveBytesModal'
import ASurveyPopup from 'shared/ASurveyPopup'
import {
  BREAKPOINTS_TO_HIDE_SIDEBAR_ON_BY_PAGE,
  DEFAULT_BREAKPOINTS_TO_HIDE_SIDEBAR_ON,
  PAGES_WITHOUT_SIDEBAR_LAYOUT,
  WIDGET_PAGES_BREAKPOINTS_TO_HIDE_SIDEBAR_ON
} from 'enums/sidebar'
import { COOKIE_ENTITY_TYPE } from 'enums/oneTrust'
import GtmNoScript from 'shared/GtmNoScript'
import { createOneTrustHandlerInstance } from 'enums/oneTrust/one-trust-handler'
import ACharWidth from 'shared/ACharWidth'
import { QUERY_PARAM } from 'enums/query-param'
import { TOP_STICKY_CONTAINER_ID } from '@/components/AHeader/enums'
import { REFS } from 'enums/external-refs'
import ASticky from 'shared/ASticky'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { removeDomainFromUrl } from '@fmpedia/helpers'
import { ROUTE_NAME } from 'enums/routes'
import ALayoutVideoBanner from '@/components/_layout/ALayoutVideoBanner/index.vue'
import {
  LAYOUT_VIDEO_BANNER_TYPE_BY_PAGE_NAME,
  PAGES_WITH_VIDEO_BANNER_ABOVE_FOOTER
} from 'enums/video-banner'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import TopBanner from '@/components/_layout/TopBanner/index.vue'
import { IL_ENVIRONMENT } from 'enums/environment'
import { RERENDER_TOP_BROKERS_COMPONENT_BUS_NAME } from '@/utils/enums/top-brokers'
import * as types from '~/store/mutation-types'
import { RERENDER_MUST_READ_COMPONENT_BUS_NAME } from '@/utils/enums/must-read'

const PAGE_WRAP_CLASS_MODIFIER = {
  NO_SIDEBAR: 'content',
  SIDEBAR1: 'content-sidebar-1',
  SIDEBAR2: 'content-sidebar-2'
}

export default {
  name: 'DefaultLayout',
  components: {
    TopBanner,
    ALayoutVideoBanner,
    ACharWidth,
    GtmNoScript,
    ANewsLetterModal,
    ALiveBytesModal,
    ASurveyPopup,
    AHeader,
    AFooter: hydrateWhenVisible(() => import('@/components/AFooter'), {
      observerOptions: { rootMargin: '100px' },
      props: ['last-modified']
    }),
    AAuthForm,
    ACrossDomainCookies,
    ATickerWidget,
    AErrorHandlerNotifier: () => import('shared/AErrorHandlerNotifier'),
    ASidebar: hydrateWhenVisible(() => import('@/components/_layout/ASidebar')),
    AGoogleAnalyticsCookiesIframe,
    ADeviceIdCookieIframe,
    ASticky
  },
  mixins: [
    mixins.windowWidth,
    mixins.googleBanners,
    mixins.setPageLoaded,
    mixins.userDetails,
    mixins.oneSignal,
    mixins.signalr,
    mixins.documentVisibility,
    mixins.internetConnectionState,
    mixins.oneTrust,
    mixins.oneTrustListener,
    mixins.actionsOnQueryParams,
    mixins.liveBytes,
    mixins.surveyRotation,
    hydrationHelpers,
    mixins.delayedContentRefresh
  ],
  head() {
    const isCookiePopupHidden = !!this.$route.query[
      QUERY_PARAM.HIDE_COOKIE_POPUP
    ]

    const oneTrustScript = {
      src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
      type: 'text/javascript',
      'data-domain-script': this.$env.IL_ONE_TRUST_SCRIPT_ID,
      defer: true
    }

    const signalRScript = {
      src: '/signalr.min.js',
      type: 'text/javascript',
      defer: true
    }

    const hideCookiePopupStyle = {
      type: 'text/css',
      innerHTML: '#onetrust-consent-sdk { display: none; }'
    }

    const isNoindexNofollow = this.$env.IL_ENVIRONMENT === IL_ENVIRONMENT.STAGE

    return {
      style: [...(isCookiePopupHidden ? [hideCookiePopupStyle] : [])],
      script: [...(this.isPreviewMode ? [] : [oneTrustScript]), signalRScript],
      ...(isNoindexNofollow
        ? {
            meta: [
              { hid: 'robots', name: 'robots', content: 'noindex, nofollow' }
            ]
          }
        : {})
    }
  },
  consts: {
    TOP_STICKY_CONTAINER_ID,
    REFS
  },
  data() {
    return {
      isDesktopHeaderMounted: false,
      isMobileHeaderMounted: false
    }
  },
  computed: {
    ...mapGetters({
      isPageLoaded: 'isPageLoaded',
      headerMenuItemsLastModified: 'headerMenuItemsLastModified',
      footerMenuItemsLastModified: 'footerMenuItemsLastModified',
      stayConnectedLastModified: 'stayConnectedLastModified',
      isPreviewMode: 'isPreviewMode',
      redirectQueue: 'router/redirectQueue',
      topBrokers: 'layouts/topBrokers',
      mustReadItems: 'sidebar/mustReadItems'
    }),
    videoBannerType() {
      return LAYOUT_VIDEO_BANNER_TYPE_BY_PAGE_NAME[this.$route.name]
    },
    isPremiumPage() {
      return this.$route.name === ROUTE_NAME.PREMIUM
    },
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    breakpointsToHideSidebarOn() {
      return (
        BREAKPOINTS_TO_HIDE_SIDEBAR_ON_BY_PAGE[this.$route.name] ||
        DEFAULT_BREAKPOINTS_TO_HIDE_SIDEBAR_ON
      )
    },
    dynamicPageWrapClass() {
      if (!this.isPageWithSidebar) {
        return [PAGE_WRAP_CLASS_MODIFIER.NO_SIDEBAR]
      }

      const isLayoutWithSidebar1 = this.$helper.compareArrays(
        DEFAULT_BREAKPOINTS_TO_HIDE_SIDEBAR_ON,
        this.breakpointsToHideSidebarOn
      )

      if (isLayoutWithSidebar1) {
        return [PAGE_WRAP_CLASS_MODIFIER.SIDEBAR1]
      }

      const isLayoutWithSidebar2 = this.$helper.compareArrays(
        WIDGET_PAGES_BREAKPOINTS_TO_HIDE_SIDEBAR_ON,
        this.breakpointsToHideSidebarOn
      )

      if (isLayoutWithSidebar2) {
        return [PAGE_WRAP_CLASS_MODIFIER.SIDEBAR2]
      }

      /** fallback default **/
      return [PAGE_WRAP_CLASS_MODIFIER.SIDEBAR1]
    },
    isVideoBannerVisible() {
      if (!this.isPageWithVideoBanner) return false

      return (
        this.isMounted &&
        this.$_oneTrust_isConsentGivenForEntity(COOKIE_ENTITY_TYPE.VIDEO_BANNER)
      )
    },
    isPageWithVideoBanner() {
      return PAGES_WITH_VIDEO_BANNER_ABOVE_FOOTER.includes(this.$route.name)
    },
    isPageWithSidebar() {
      return !PAGES_WITHOUT_SIDEBAR_LAYOUT.includes(this.$route.name)
    },
    footerLastModified() {
      return this.footerMenuItemsLastModified + this.stayConnectedLastModified
    },
    isConsentGivenForTwTicker() {
      return this.$_oneTrust_isConsentGivenForEntity(
        COOKIE_ENTITY_TYPE.TW_TICKER
      )
    },
    globalWrapDynamicClass() {
      return {
        'preview-mode': this.isPreviewMode,
        'premium-page': this.isPremiumPage,
        'no-tw-ticker': !this.isConsentGivenForTwTicker
      }
    },
    tickerDynamicClass() {
      return {
        hidden: this.isPremiumPage
      }
    }
  },
  methods: {
    ...mapActions({
      requestAuthStatus: 'auth/requestAuthStatus',
      registerHandlerThatRequiresConsent:
        'one-trust/registerHandlerThatRequiresConsent',
      removeAllNavigationExcept: 'router/removeAllNavigationExcept',
      requestTopBrokers: 'layouts/requestTopBrokers',
      requestMustReadItems: 'sidebar/requestMustReadItems'
    }),
    ...mapMutations({
      setTopBrokers: `layouts/${types.SET_TOP_BROKERS}`,
      setMustReadItems: `sidebar/${types.SET_MUST_READ_ITEMS}`
    }),
    registerBeforeUnloadListener() {
      /** Such an approach technique as to define - is page loaded in newly opened tab, or as result of tab reloading.
       * What do we do:
       * On page reload - we set a cookie "PAGE_RELOADED".
       * By checking this cookie - we can define, is page reloaded in current tab, or page is opened in new tab.
       * (cookie is accessible in serverMiddleware, nuxtServerInit, asyncData, etc).
       * "PAGE_RELOADED" cookie is sent to the server on page reload, and then expired and removed from browser in 3 seconds. **/

      window.addEventListener('beforeunload', function() {
        const date = new Date()
        const expiringOffset = 3 * 1000
        date.setTime(date.getTime() + expiringOffset)
        Cookies.set(COOKIE.PAGE_RELOADED, COOKIE.PAGE_RELOADED, {
          expires: date
        })
      })
    },
    handleRouterQueueCollisions() {
      const currentUrl = window.location.href
      const currentPath = removeDomainFromUrl(
        currentUrl,
        this.$env.IL_DOMAIN_URL
      )
      const latestRedirect = this.redirectQueue[this.redirectQueue.length - 1]

      if (!latestRedirect) return

      const latestRedirectPath = latestRedirect.fullPath
      /**
       * Back/Forward button clicked but beforeEach wasn't called due to duplicate
       * route in navigation (cached)
       */
      if (currentPath !== latestRedirectPath) {
        this.removeAllNavigationExcept(currentPath)
      }
    },
    refreshTopBrokersData() {
      this.$_delayedContentRefresh_run({
        getOldDataFn: () => this.topBrokers,
        requestNewDataFn: this.requestTopBrokers,
        dataChangedCallbackFn: newData => {
          this.setTopBrokers(newData)
          this.$bus.$emit(RERENDER_TOP_BROKERS_COMPONENT_BUS_NAME)
        }
      })
    },
    refreshMustReadData() {
      this.$_delayedContentRefresh_run({
        getOldDataFn: () => this.mustReadItems,
        requestNewDataFn: this.requestMustReadItems,
        dataChangedCallbackFn: ({ data, lastModified }) => {
          this.setMustReadItems({ data, lastModified })
          this.$bus.$emit(RERENDER_MUST_READ_COMPONENT_BUS_NAME)
        }
      })
    }
  },
  mounted() {
    this.requestAuthStatus()
    addEventListener('popstate', this.handleRouterQueueCollisions)

    this.registerHandlerThatRequiresConsent(
      createOneTrustHandlerInstance({
        handler: this.registerBeforeUnloadListener,
        entityType: COOKIE_ENTITY_TYPE.PAGE_RELOADED_COOKIE
      })
    )

    this.refreshTopBrokersData()
    this.refreshMustReadData()
  },
  beforeDestroy() {
    removeEventListener('popstate', this.handleRouterQueueCollisions)
  }
}
</script>

<style lang="scss" scoped></style>
